import { useMelwaysInfo, useMelwaysLink } from '@seek/melways-react';
import { useTranslations } from '@vocab/react';
import { Card, Stack } from 'braid-design-system';
import { useState, type ComponentProps } from 'react';

import { ApplyLink } from 'src/components/ApplyLink/ApplyLink';
import { DeleteDialog } from 'src/components/DeleteDialog/DeleteDialog';
import { GeneralDetails } from 'src/components/GeneralDetails/GeneralDetails';
import translations from 'src/components/JobItemLayout/JobItemLayout.vocab';
import { MenuAction } from 'src/components/MenuAction/MenuAction';
import { Notes } from 'src/components/Notes/Notes';
import { JOB_TYPE } from 'src/const/app';
import { analyticsClient } from 'src/modules/analytics/analytics';
import type { AppliedJob, SavedJob } from 'src/types/graphql';
import { getJobStatus } from 'src/utils';

interface DeleteMyActivityJob {
  variables: {
    input: { id: string };
    locale: string;
  };
}
export interface Props {
  index: number;
  errorMessage: ComponentProps<typeof Stack>['children'];
  dateApplied?: ComponentProps<typeof Stack>['children'];
  job: AppliedJob | SavedJob;
  type: 'applied' | 'saved';
  deleteMyActivityJob: ({
    variables: {
      input: { id },
      locale,
    },
  }: DeleteMyActivityJob) => void;
  openView?: (id: string) => void;
  openNotes: (id: string) => void;
}

export const JobItemLayout = ({
  index,
  errorMessage,
  dateApplied,
  job,
  type,
  deleteMyActivityJob,
  openView,
  openNotes,
}: Props) => {
  const { t } = useTranslations(translations);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { id, isExternal, notes, isActive } = job;
  const isPrivateAdvertiser = job.job?.tracking?.isPrivateAdvertiser ?? false;
  const { locale } = useMelwaysInfo();
  const generalDetailsLink = useMelwaysLink(
    `/${!isActive ? 'expired' : ''}job/${id}?ref=${type}`,
  );
  const applyLink = useMelwaysLink(`/job/${id}/apply`);

  const handleDelete = () => {
    setShowDeleteDialog(true);
  };
  const processDelete = () => {
    setShowDeleteDialog(false);
    deleteMyActivityJob({
      variables: {
        input: { id },
        locale,
      },
    });
    analyticsClient.trackLink({
      eventName: 'job_unsave_pressed',
      jobStatus: getJobStatus(job?.isActive).toLowerCase(),
      jobIsLinkOut: job?.isExternal,
      jobId: job?.id,
      jobListingDate: job?.job.createdAt.dateTimeUtc,
      jobLocation: job?.job.location.label,
      jobSalary: job?.job.salary?.label,
      jobTitle: job?.job.title,
      advertiserName: job?.job.advertiser.name,
    });
  };

  const shouldHideApplyLinkForPrivateJobsInHK =
    !isExternal && locale.includes('-HK') && isPrivateAdvertiser;

  return (
    <>
      <DeleteDialog
        index={index}
        showDeleteDialog={showDeleteDialog}
        setShowDeleteDialog={setShowDeleteDialog}
        processDelete={processDelete}
        title={t('Delete job')}
        message={
          type === JOB_TYPE.applied
            ? t('This application will')
            : t(
                `Are you sure you would like to delete this job from your saved jobs list?`,
              )
        }
      />
      <Card data={{ automation: `job-item-${index}` }}>
        <Stack space="large">
          <Stack space="large">
            {errorMessage}
            <GeneralDetails
              link={generalDetailsLink}
              isActive={isActive}
              title={job.job.title}
              listedDate={job.job.createdAt.label}
              advertiser={job.job.advertiser.name}
              location={job.job.location.label}
              salary={job.job.salary?.label}
              currencyLabel={job.job.salary?.currencyLabel ?? undefined}
              abstract={job.job.abstract}
              type={type}
              handleOpenViewApplication={
                openView ? () => openView(job.id) : () => {}
              }
              menu={
                <MenuAction
                  type={type}
                  index={index}
                  isWithNotes={Boolean(notes)}
                  handleDelete={handleDelete}
                  handleEdit={() => openNotes(job.id)}
                  handleOpenViewApplication={
                    openView ? () => openView(job.id) : () => {}
                  }
                />
              }
              actions={
                type === JOB_TYPE.saved &&
                isActive &&
                !shouldHideApplyLinkForPrivateJobsInHK ? (
                  <ApplyLink
                    applyLink={applyLink}
                    isExternal={isExternal}
                    job={job as SavedJob}
                    index={index}
                  />
                ) : null
              }
            />
            {dateApplied ? dateApplied : null}
          </Stack>
          {notes ? (
            <Notes
              index={index}
              notes={notes}
              handleOpen={() => openNotes(job.id)}
            />
          ) : null}
        </Stack>
      </Card>
    </>
  );
};
